import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import Brand from "@/components/Brand";
import {
  LogOut, Plus, Upload, Download, Search, Trash2, Edit2, LogOut as ForceLogout, Save, X, ShieldCheck, Users,
} from "lucide-react";
export default function AdminPanel() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [q, setQ] = useState("");

  const [showAdd, setShowAdd] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newLifetime, setNewLifetime] = useState(false);
  const [newExpiry, setNewExpiry] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    return d.toISOString().slice(0, 10);
  });

  const [editingId, setEditingId] = useState(null);
  const [editingExpiry, setEditingExpiry] = useState("");
  const [editingLifetime, setEditingLifetime] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(null); // {id, email}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/whitelist");
      setItems(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const showToast = (m) => {
    setToast(m);
    setTimeout(() => setToast(""), 2500);
  };

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/whitelist", {
        email: newEmail,
        expiry_date: newLifetime ? null : newExpiry,
      });
      setNewEmail("");
      setNewLifetime(false);
      setShowAdd(false);
      showToast("Ahli baharu ditambah");
      load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const saveEdit = async (id) => {
    await api.patch(`/admin/whitelist/${id}`, {
      lifetime: editingLifetime || undefined,
      expiry_date: editingLifetime ? null : editingExpiry,
    });
    setEditingId(null);
    setEditingLifetime(false);
    showToast("Dikemaskini");
    load();
  };

  const del = async (id) => {
    try {
      await api.delete(`/admin/whitelist/${id}`);
      setConfirmDelete(null);
      showToast("Ahli dipadam");
      load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setConfirmDelete(null);
    }
  };

  const forceLogout = async (id) => {
    try {
      await api.post(`/admin/whitelist/${id}/force-logout`);
      showToast("Sesi diberhentikan");
      load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const handleImport = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/whitelist/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const errMsg = (data.errors || []).length ? ` · ${data.errors.length} baris ditolak` : "";
      showToast(`Import: +${data.inserted} baru, ~${data.updated} kemaskini${errMsg}`);
      load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const downloadCsvTemplate = () => {
    const csv = "email,expiry_date\nahli1@example.com,2026-12-31\nahli2@example.com,2027-06-30\nahli.lifetime@example.com,lifetime\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "faceclub-whitelist-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExport = async () => {
    const token = localStorage.getItem("admin_token");
    const res = await fetch(`${API}/admin/whitelist/export`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "whitelist.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_email");
    navigate("/admin/login");
  };

  const filtered = items.filter((i) => !q || i.email.toLowerCase().includes(q.toLowerCase()));
  const today = new Date().toISOString().slice(0, 10);
  const activeCount = items.filter((i) => i.session_id).length;
  const expiredCount = items.filter((i) => i.expiry_date && i.expiry_date < today).length;
  const lifetimeCount = items.filter((i) => !i.expiry_date).length;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Brand size="sm" showTagline={false} />
            <span className="hidden sm:inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-amber-500/80 font-mono">
              <ShieldCheck size={12} /> Pentadbir
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Link to="/admin" className="px-3 py-2 rounded-md text-sm text-neutral-400 hover:text-white hover:bg-white/5" data-testid="admin-nav-dashboard">Dashboard</Link>
            <Link to="/admin/whitelist" className="px-3 py-2 rounded-md text-sm bg-amber-500/10 text-amber-400" data-testid="admin-nav-whitelist">Whitelist</Link>
            <button
              onClick={logout}
              className="ml-2 flex items-center gap-2 px-3 py-2 rounded-md text-sm text-neutral-400 hover:text-amber-400 hover:bg-white/5 transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut size={16} /> Keluar
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-amber-500/80 font-semibold mb-2">Whitelist</div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold">Senarai Ahli</h1>
          </div>
          <div className="flex gap-3 flex-wrap">
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => handleImport(e.target.files?.[0])}
              data-testid="csv-import-input"
            />
            <button
              onClick={() => fileRef.current?.click()}
              className="flex items-center gap-2 px-4 py-2.5 rounded-md border border-white/10 text-sm text-neutral-300 hover:bg-white/5 transition-colors"
              data-testid="csv-import-btn"
            >
              <Upload size={16} /> Import CSV
            </button>
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2.5 rounded-md border border-white/10 text-sm text-neutral-300 hover:bg-white/5 transition-colors"
              data-testid="csv-export-btn"
            >
              <Download size={16} /> Eksport CSV
            </button>
            <button
              onClick={() => setShowAdd(true)}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="add-whitelist-btn"
            >
              <Plus size={16} /> Tambah Ahli
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <Stat label="Jumlah Ahli" value={items.length} icon={<Users size={18} />} />
          <Stat label="Sesi Aktif" value={activeCount} accent />
          <Stat label="Selamanya" value={lifetimeCount} />
          <Stat label="Tamat Tempoh" value={expiredCount} danger />
        </div>

        {/* CSV Import Panel — selalu visible */}
        <div className="bg-[#101010] border border-amber-500/20 rounded-xl p-5 mb-6" data-testid="csv-import-panel">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-amber-500/10 border border-amber-500/30 shrink-0">
              <Upload className="text-amber-400" size={20} />
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="font-display text-lg font-semibold mb-1">Muat Naik Senarai Ahli (CSV)</div>
              <p className="text-sm text-neutral-400 mb-2">
                Format: dua lajur dengan header <code className="px-1.5 py-0.5 bg-black/60 rounded text-amber-400 font-mono text-xs">email,expiry_date</code> · <span className="text-neutral-500">tarikh ikut format </span><code className="px-1.5 py-0.5 bg-black/60 rounded text-amber-400 font-mono text-xs">YYYY-MM-DD</code> · <span className="text-neutral-500">guna </span><code className="px-1.5 py-0.5 bg-black/60 rounded text-amber-400 font-mono text-xs">lifetime</code><span className="text-neutral-500"> atau kosong untuk akses selamanya</span>
              </p>
              <pre className="text-xs text-neutral-500 font-mono bg-black/40 rounded-md px-3 py-2 border border-white/5 overflow-x-auto">
{`email,expiry_date
ali@example.com,2026-12-31
abu@example.com,2027-06-30
vip@example.com,lifetime`}
              </pre>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <button
                onClick={() => fileRef.current?.click()}
                className="btn-primary inline-flex items-center justify-center gap-2 w-full"
                data-testid="csv-import-primary-btn"
              >
                <Upload size={16} /> Pilih CSV
              </button>
              <button
                onClick={downloadCsvTemplate}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-white/10 text-xs text-neutral-300 hover:bg-white/5 transition-colors"
                data-testid="csv-template-download"
              >
                <Download size={14} /> Template Kosong
              </button>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500" size={18} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari mengikut emel…"
            className="w-full pl-12 pr-4 py-3 bg-[#101010] border border-white/10 rounded-md text-white font-mono text-sm focus:border-amber-500/40 focus:outline-none"
            data-testid="admin-search-input"
          />
        </div>

        {error && (
          <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-md px-4 py-3 mb-4">
            {error}
          </div>
        )}

        <div className="bg-[#101010] border border-white/10 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="whitelist-table">
              <thead>
                <tr className="bg-[#141414] border-b border-white/10 text-left">
                  <th className="px-5 py-3 text-xs uppercase tracking-wider text-neutral-500 font-mono">Emel</th>
                  <th className="px-5 py-3 text-xs uppercase tracking-wider text-neutral-500 font-mono">Tamat</th>
                  <th className="px-5 py-3 text-xs uppercase tracking-wider text-neutral-500 font-mono">Status</th>
                  <th className="px-5 py-3 text-xs uppercase tracking-wider text-neutral-500 font-mono">Akhir Login</th>
                  <th className="px-5 py-3 text-xs uppercase tracking-wider text-neutral-500 font-mono text-right">Tindakan</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="px-5 py-8 text-center text-neutral-500">Memuatkan…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={5} className="px-5 py-8 text-center text-neutral-500">Tiada ahli.</td></tr>
                ) : filtered.map((it) => {
                  const lifetime = !it.expiry_date;
                  const expired = !lifetime && it.expiry_date < today;
                  const active = !!it.session_id;
                  return (
                    <tr key={it.id} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-testid={`whitelist-row-${it.id}`}>
                      <td className="px-5 py-3 text-white font-mono">{it.email}</td>
                      <td className="px-5 py-3">
                        {editingId === it.id ? (
                          <div className="flex items-center gap-2 flex-wrap">
                            <label className="flex items-center gap-1.5 text-xs text-amber-300 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={editingLifetime}
                                onChange={(e) => setEditingLifetime(e.target.checked)}
                                className="accent-amber-500"
                                data-testid={`lifetime-toggle-${it.id}`}
                              />
                              Selamanya
                            </label>
                            <input
                              type="date"
                              value={editingExpiry}
                              onChange={(e) => setEditingExpiry(e.target.value)}
                              disabled={editingLifetime}
                              className="px-2 py-1 bg-[#0A0A0A] border border-white/10 rounded text-white text-xs font-mono disabled:opacity-40"
                              data-testid={`expiry-input-${it.id}`}
                            />
                          </div>
                        ) : lifetime ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-amber-500/15 text-amber-300 border border-amber-500/40 uppercase tracking-wider font-mono">
                            ∞ Selamanya
                          </span>
                        ) : (
                          <span className={`font-mono text-xs ${expired ? "text-red-400" : "text-neutral-300"}`}>
                            {it.expiry_date}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        {expired ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-red-500/10 text-red-400 border border-red-500/20 uppercase tracking-wider font-mono">Tamat</span>
                        ) : active ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-amber-500/10 text-amber-400 border border-amber-500/30 uppercase tracking-wider font-mono">Aktif</span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-white/5 text-neutral-400 border border-white/10 uppercase tracking-wider font-mono">Idle</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-xs text-neutral-500 font-mono">
                        {it.last_login_at ? new Date(it.last_login_at).toLocaleString("ms-MY") : "—"}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {editingId === it.id ? (
                            <>
                              <button
                                onClick={() => saveEdit(it.id)}
                                className="p-1.5 rounded text-amber-400 hover:bg-amber-500/10"
                                data-testid={`save-${it.id}`}
                              >
                                <Save size={14} />
                              </button>
                              <button onClick={() => { setEditingId(null); setEditingLifetime(false); }} className="p-1.5 rounded text-neutral-500 hover:bg-white/5">
                                <X size={14} />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => {
                                  setEditingId(it.id);
                                  setEditingExpiry(it.expiry_date || new Date().toISOString().slice(0, 10));
                                  setEditingLifetime(!it.expiry_date);
                                }}
                                className="p-1.5 rounded text-neutral-400 hover:text-amber-400 hover:bg-amber-500/10"
                                title="Edit tarikh"
                                data-testid={`edit-${it.id}`}
                              >
                                <Edit2 size={14} />
                              </button>
                              <button
                                onClick={() => forceLogout(it.id)}
                                disabled={!active}
                                className="p-1.5 rounded text-neutral-400 hover:text-amber-400 hover:bg-amber-500/10 disabled:opacity-30 disabled:cursor-not-allowed"
                                title="Force logout"
                                data-testid={`force-logout-${it.id}`}
                              >
                                <ForceLogout size={14} />
                              </button>
                              <button
                                onClick={() => setConfirmDelete({ id: it.id, email: it.email })}
                                className="p-1.5 rounded text-neutral-400 hover:text-red-400 hover:bg-red-500/10"
                                title="Padam"
                                data-testid={`delete-${it.id}`}
                              >
                                <Trash2 size={14} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={add}
            className="bg-[#101010] border border-white/10 rounded-xl p-6 w-full max-w-md shadow-2xl"
            data-testid="add-whitelist-modal"
          >
            <h3 className="font-display text-2xl font-bold mb-1">Tambah Ahli</h3>
            <p className="text-sm text-neutral-500 mb-5">Berikan emel dan tarikh tamat akses.</p>
            <label className="block text-xs text-neutral-500 mb-1 font-mono uppercase">Emel</label>
            <input
              type="email"
              required
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-white/10 rounded-md text-white font-mono text-sm mb-4 focus:border-amber-500/40 focus:outline-none"
              data-testid="add-email-input"
            />
            <label className="block text-xs text-neutral-500 mb-1 font-mono uppercase">Tarikh Tamat</label>
            <div className="mb-3">
              <label className="flex items-center gap-2 cursor-pointer mb-2">
                <input
                  type="checkbox"
                  checked={newLifetime}
                  onChange={(e) => setNewLifetime(e.target.checked)}
                  className="accent-amber-500"
                  data-testid="add-lifetime-toggle"
                />
                <span className="text-sm text-amber-300">
                  ∞ Akses Selamanya <span className="text-neutral-500">(tiada tarikh tamat)</span>
                </span>
              </label>
            </div>
            <input
              type="date"
              required={!newLifetime}
              disabled={newLifetime}
              value={newExpiry}
              onChange={(e) => setNewExpiry(e.target.value)}
              className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-white/10 rounded-md text-white font-mono text-sm mb-5 focus:border-amber-500/40 focus:outline-none disabled:opacity-40"
              data-testid="add-expiry-input"
            />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 rounded-md text-neutral-300 hover:bg-white/5">
                Batal
              </button>
              <button type="submit" className="btn-primary" data-testid="add-submit-btn">
                Tambah
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Confirm delete modal */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setConfirmDelete(null)}
          data-testid="confirm-delete-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-[#101010] border border-red-500/30 rounded-xl p-6 w-full max-w-md shadow-2xl"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center justify-center">
                <Trash2 className="text-red-400" size={18} />
              </div>
              <h3 className="font-display text-xl font-bold">Padam Ahli?</h3>
            </div>
            <p className="text-sm text-neutral-400 mb-5">
              Ahli <span className="text-white font-mono">{confirmDelete.email}</span> akan dipadam kekal dari whitelist. Tindakan ini tidak boleh dibatalkan.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 rounded-md text-neutral-300 hover:bg-white/5"
                data-testid="cancel-delete-btn"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={() => del(confirmDelete.id)}
                className="px-4 py-2 rounded-md bg-red-500 text-white font-semibold hover:bg-red-400 transition-colors"
                data-testid="confirm-delete-btn"
              >
                Ya, Padam
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 bg-[#141414] border border-amber-500/30 px-5 py-3 rounded-lg shadow-2xl shadow-black/60 text-sm" data-testid="admin-toast">
          {toast}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, icon, accent, danger }) {
  return (
    <div className={`bg-[#101010] border rounded-xl p-5 ${danger ? "border-red-500/30" : accent ? "border-amber-500/30" : "border-white/10"}`}>
      <div className="text-xs text-neutral-500 uppercase tracking-wider font-mono flex items-center gap-2">
        {icon}
        {label}
      </div>
      <div className={`font-display text-4xl font-bold mt-2 ${danger ? "text-red-400" : accent ? "text-amber-400" : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}
